import anchorPoints from './anchorPoints'
import getCoordinates from './getCoordinates'

const {
  TOP_LEFT,
  TOP,
  TOP_RIGHT,
  RIGHT_TOP,
  RIGHT,
  RIGHT_BOTTOM,
  BOTTOM_LEFT,
  BOTTOM,
  BOTTOM_RIGHT,
  LEFT_TOP,
  LEFT,
  LEFT_BOTTOM
} = anchorPoints

/**
 * @typedef {Object} RelativePosition
 * @property {string} direction
 * @property {?string} align
 * @property {?number} offset
 */

/**
 * @typedef {Object} Position
 * @property {number} left
 * @property {number} top
 */

/**
 * @param {Element} element
 * @param {Element} relativeToElement
 * @param {RelativePosition} position
 * @param {?boolean} inViewport Should it be kept inside viewport
 * @param {?boolean} relative Should the element be relative to it's original position
 */
export default function positionElement (
  element,
  relativeToElement,
  position,
  inViewport = false,
  relative = false
) {
  const {
    position: calculatedPosition,
    containerPosition,
    relativeToElementBounds
  } = calculateRelativePosition(
    position,
    element,
    relativeToElement,
    inViewport
  )

  let top = containerPosition.top
  let left = containerPosition.left

  if (!relative) {
    const { top: offsetTop, left: offsetLeft } = element.offsetParent
      ? element.offsetParent.getBoundingClientRect()
      : { top: 0, left: 0 }
    top += relativeToElementBounds.top - offsetTop
    left += relativeToElementBounds.left - offsetLeft
  }
  element.style.top = `${top}px`
  element.style.left = `${left}px`

  return calculatedPosition
}

/**
 * Create a shallow copy of an object.
 *
 * @param {!Object<string, any>}
 * @returns {Object<string, any>}
 */
function clone (a) {
  const obj = {}
  for (const key in a) {
    obj[key] = a[key]
  }
  return obj
}

/**
 * @param {RelativePosition} position
 * @param {Element} element
 * @param {Element} relativeToElement
 * @param {?boolean} inViewport
 * @returns {RelativePosition}
 */
function calculateRelativePosition (
  position,
  element,
  relativeToElement,
  inViewport
) {
  // reset the position so that the wrapper is laid out by the browser
  // and we are able to compute the correct height and width
  element.style.top = '0'
  element.style.left = '0'

  const relativeToElementBounds = clone(
    relativeToElement.getBoundingClientRect()
  )
  const elementBounds = element.getBoundingClientRect()
  const { direction, align, offset } = position
  const anchorPoint = getAnchorPoint(direction, align)
  const leftOffset =
    (offset &&
      {
        [RIGHT]: offset,
        [LEFT]: -offset
      }[align]) ||
    0
  const topOffset =
    (offset &&
      {
        [BOTTOM]: offset,
        [TOP]: -offset
      }[align]) ||
    0

  if (leftOffset) {
    relativeToElementBounds.left += leftOffset
  }
  if (topOffset) {
    relativeToElementBounds.top += topOffset
  }
  const {
    anchorPoint: calculatedAnchorPoint,
    containerPosition
  } = getCoordinates({
    viewportRect: getViewportRect(
      inViewport ? document.documentElement : document.body
    ),
    panelRect: elementBounds,
    actionRect: relativeToElementBounds,
    anchorPoint,
    fallbackAnchorPoints: getFallbackAnchorPoints(position)
  })
  const [calculatedDirection, calculatedAlign] = calculatedAnchorPoint.split(
    '-'
  )
  return {
    position: {
      direction: calculatedDirection,
      align: calculatedAlign,
      offset
    },
    containerPosition,
    relativeToElementBounds
  }
}

/**
 * @param {string} direction
 * @param {string} align
 * @returns {string}
 */
function getAnchorPoint (direction, align) {
  return [direction, align].filter(v => v).join('-')
}

/**
 * @param {RelativePosition} param0
 * @returns {string[]}
 */
function getFallbackAnchorPoints ({ direction, align }) {
  const anchorPoint = getAnchorPoint(direction, align)
  function _getFallbackAnchorPoints (direction) {
    switch (direction) {
      case BOTTOM_LEFT:
      case BOTTOM:
      case BOTTOM_RIGHT:
        return [
          BOTTOM,
          BOTTOM_LEFT,
          BOTTOM_RIGHT,
          getAnchorPoint(TOP, align),
          TOP,
          align
        ]
      case TOP:
      case TOP_RIGHT:
      case TOP_LEFT:
        return [
          TOP,
          TOP_LEFT,
          TOP_RIGHT,
          getAnchorPoint(BOTTOM, align),
          BOTTOM,
          align
        ]
      case RIGHT_BOTTOM:
      case RIGHT:
      case RIGHT_TOP:
        return [
          RIGHT,
          RIGHT_TOP,
          RIGHT_BOTTOM,
          BOTTOM_LEFT,
          BOTTOM,
          BOTTOM_RIGHT,
          TOP_LEFT,
          TOP,
          TOP_RIGHT,
          getAnchorPoint(LEFT, align)
        ]
      case LEFT:
      case LEFT_BOTTOM:
      case LEFT_TOP:
        return [
          LEFT,
          LEFT_TOP,
          LEFT_BOTTOM,
          BOTTOM_RIGHT,
          BOTTOM,
          BOTTOM_LEFT,
          TOP_RIGHT,
          TOP,
          TOP_LEFT,
          getAnchorPoint(RIGHT, align)
        ]
      default:
        return []
    }
  }
  return _getFallbackAnchorPoints(anchorPoint)
    .filter(d => d && d !== anchorPoint)
    .filter(isFirstInArray)
}

/**
 * Checks if value's index appearance in the array is same as the first (to remove duplicates)
 * @param {*} value
 * @param {number} index
 * @param {Array} array
 */
function isFirstInArray (value, index, array) {
  return array.indexOf(value) === index
}

/**
 * @returns {DOMRect}
 */
function getViewportRect (element = document.documentElement) {
  const {
    clientWidth: width,
    clientHeight: height,
    scrollTop: x = 0,
    scrollLeft: y = 0
  } = element

  return {
    width,
    height,
    top: 0,
    right: width,
    bottom: Math.max(document.defaultView.innerHeight, height),
    left: 0,
    x,
    y
  }
}
